import React, { useState, useCallback, useEffect } from "react"
import { CircularProgress, AutocompleteProps } from "@mui/material"
import { useGetCountriesLazyQuery, Vendor } from "src/graphql/quark/quark.type"
import InputAutocomplete from "src/shared/components/input-autocomplete"

interface Option {
  id: string
  name: string
}

const SelectCountryField: React.FC<
  {
    onChange: (val: string | string[]) => void
    value?: string | string[] | null
    multiple?: boolean
    error?: boolean
    showChip?: "outside" | "inside"
    helperText?: string
    vendor?: Vendor
    label?: string
    placeholder?: string
    required?: boolean
  } & Omit<AutocompleteProps<any, any, any, any>, "renderInput" | "options">
> = ({ onChange, value, error, multiple, helperText, vendor, showChip, label, placeholder, required, ...rest }) => {
  const [options, setOptions] = useState<Option[]>([])
  const [loading, setLoading] = useState(false)
  const [getCountries, { error: getError }] = useGetCountriesLazyQuery()

  useEffect(() => {
    if (value && !options.length) fetchOptions()
  }, [value])
  const fetchOptions = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getCountries()

      if (!response.data?.getCountries.length) return null
      const resData: Option[] = response.data?.getCountries?.map((val: any) => ({
        id: val.id as string,
        name: val.name as string
      }))
      setOptions(resData)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }, [loading])

  return (
    <InputAutocomplete
      {...rest}
      options={options}
      multiple={multiple}
      getOptionKey={(option) => option?.id ?? ""}
      getOptionLabel={(option) => option.name ?? ""}
      onOpen={() => fetchOptions()}
      loading={loading}
      value={multiple ? options.filter((val) => value?.includes(val.id)) : options.find((val) => val.id === value)}
      onChange={(val: any) => {
        onChange(multiple ? val?.map((val: any) => val.id) : val?.id)
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name}
        </li>
      )}
      showChip={showChip}
      label={label}
      placeholder={placeholder}
      required={required}
      loadingText={<CircularProgress size={20} />}
      error={error ?? !!getError?.message}
      helperText={helperText ?? getError?.message}
    />
  )
}

export default SelectCountryField
